// Disabling sort-key for fields that follow the expected order of columns in the generated report
/* eslint-disable sort-keys */

import { User_userPermissions } from "@saleor/fragments/types/User";
import { formatTextData, getUserFullName } from "@saleor/reports/utils/misc";
import {
  PermissionEnum,
  SettlementStatusEnum
} from "@saleor/types/globalTypes";
import moment from "moment";

import { PayoutOrdersDownload_storeOrdersPayout_edges_node } from "../types/PayoutOrdersDownload";

interface ReportStructure {
  filename: string;
  fields: {};
  permission: PermissionEnum;
  title: string;
}

export const getReportStructure = (
  permissions: User_userPermissions[],
  status: SettlementStatusEnum
) => {
  const permissionCodes = permissions.map(permission => permission.code);
  const timestamp = moment(new Date()).format("YYYYMMDDHHmmss");
  const user = getUserFullName();

  const reportStructures: ReportStructure[] = [
    {
      filename: `Open_Orders_${user}_${timestamp}`,
      fields: {
        id: "ID",
        mall_name: "Mall",
        legal_name: "Company Name",
        brand_name: "Brand",
        vendor_code: "Vendor Code",
        order_date: "Order Created Date",
        completed_at: "Order Completion Date",
        store_order_code: "Store Order Code",
        payment_method_type: "Payment Method",
        retail_price: "Total Retail Price",
        sales_discount_tenant: "Discount - Tenant",
        sales_discount_sm: "Discount - SMO",
        sales_discount_total: "Total - Discount",
        selling_price: "Total Selling Price",
        delivery_fee: "Delivery Fee",
        total_gross_transaction: "Total Gross Transaction",
        voucher_discount_tenant: "Voucher - Tenant",
        voucher_discount_sm: "Voucher - SMO",
        voucher_discount_total: "Total - Voucher",
        price_paid: "Price Paid",
        payment_fee: "Payment Fee",
        payment_fee_vat: "VAT on Payment Fee",
        payment_fee_adjustment: "Adjustment on Payment Fee",
        payment_fee_adjustment_vat: "Adjustment VAT on Payment Fee",
        other_adjustment: "Other Adjustment",
        other_remarks: "Other Adjustment Remarks",
        penalties: "Penalties",
        delivery_fee_adjustment: "Adjustment on Delivery Fee",
        total_payment_fee: "Total Payment Fee",
        payment_fee_witholding_tax: "Withholding Tax on Payment Fee",
        credit_card_witholding_tax:
          "Creditable Withholding Tax on Card Transaction",
        in_store_cash_payment: "In Store Cash Payment",
        net_settlement: "Net Settlement",
        settlement_date: "Settlement Date",
        payout_status: "Payout Status",
        shipping_cost: "Shipping Cost (Actual)",
        rebate_amount: "Rebate",
        insurance: "Insurance",
        transaction_income: "Transaction Income / (Loss)",
        payment_fee_2307: "2307 on Payment Fee Status",
        mdr: "MDR",
        payment_gateway: "Payment Gateway",
        cash_handling_fee: "Cash Handling Fee"
      },
      permission: PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER,
      title: "Open Orders"
    }
  ];

  const userReportStructures = reportStructures.filter(r =>
    permissionCodes.includes(r.permission)
  );

  const title =
    status === SettlementStatusEnum.OPEN
      ? "Open Orders"
      : status === SettlementStatusEnum.FOR_PAYMENT
      ? "For Payment Orders"
      : "Excluded Orders";

  if (userReportStructures?.length >= 1) {
    return { ...userReportStructures[0], title };
  }

  return { ...reportStructures[0], title };
};

export const selectReportData = (
  reportData: PayoutOrdersDownload_storeOrdersPayout_edges_node[],
  reportStructure: ReportStructure
): Array<{}> => {
  const reportDataClone = reportData.map(object => ({ ...object }));

  const selectedReportData = reportDataClone.map(data => {
    delete data.__typename;

    data.id = atob(data.id);

    const currencyFields = [
      "retail_price",
      "sales_discount_tenant",
      "sales_discount_total",
      "selling_price",
      "delivery_fee",
      "total_gross_transaction",
      "voucher_discount_tenant",
      "voucher_discount_sm",
      "voucher_discount_total",
      "price_paid",
      "payment_fee",
      "payment_fee_vat",
      "payment_fee_adjustment",
      "payment_fee_adjustment_vat",
      "other_adjustment",
      "penalties",
      "delivery_fee_adjustment",
      "total_payment_fee",
      "payment_fee_witholding_tax",
      "credit_card_witholding_tax",
      "net_settlement",
      "shipping_cost",
      "rebate_amount",
      "insurance",
      "transaction_income",
      "payment_fee_2307",
      "mdr",
      "payment_gateway",
      "cash_handling_fee"
    ];

    const textFields = [
      "legal_name",
      "mall_name",
      "brand_name",
      "payment_method_type"
    ];

    Object.entries(data).forEach(([key, value]) => {
      // Converts nulls to empty string
      if (data[key] === null) {
        data[key] = "";
        return;
      }

      if (currencyFields.includes(key)) {
        data[key] =
          data[key] !== null ? Math.round((data[key] || 0) * 100) / 100 : "";
        return;
      }

      if (textFields.includes(key)) {
        data[key] = formatTextData(`"${value}"`, "UPC");
        return;
      }

      if (textFields.includes(key)) {
        data[key] = formatTextData(value, "UPC");
        return;
      }

      // Encloses string with double quotes
      if (typeof data[key] === "string" && data[key].includes(",")) {
        data[key] = `"${data[key]}"`;
      }
    });

    data.order_date = data?.order_date
      ? moment
          .tz(new Date(data?.order_date), "Asia/Manila")
          .format("YYYY-MM-DD HH:mm:ss")
      : "";

    data.completed_at = data?.completed_at
      ? moment
          .tz(new Date(data?.completed_at), "Asia/Manila")
          .format("YYYY-MM-DD HH:mm:ss")
      : "";

    data.settlement_date = data?.settlement_date
      ? moment
          .tz(new Date(data?.settlement_date), "Asia/Manila")
          .format("YYYY-MM-DD HH:mm:ss")
      : "";

    return Object.keys(data)
      .filter(key => Object.keys(reportStructure.fields).includes(key))
      .reduce(
        (cur, key) => Object.assign(cur, { [key]: String(data[key]) }),
        {}
      );
  });

  return selectedReportData;
};
